/**
 * Centralized format conventions (dates, numbers, etc.) for the app.
 * Use these so we can change conventions (e.g. locale) in one place later.
 */

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function parseDateParts(dateStr) {
  if (dateStr == null) return null;
  const str = typeof dateStr === 'string' ? dateStr : (dateStr?.toString?.() ?? '');
  const parts = str.split('T')[0].split('-');
  const [y, m, d] = parts;
  if (!y || !m || !d) return null;
  const monthIdx = parseInt(m, 10) - 1;
  if (monthIdx < 0 || monthIdx > 11) return null;
  return { year: y, month: m, monthIdx, day: parseInt(d, 10) };
}

/**
 * Format a date string (ISO date or datetime) for display.
 * @param {string|*} dateStr - Date value (e.g. '2026-02-28' or '2026-02-28T00:00:00Z')
 * @param {'full'|'display'|'chart'} format - 'display': "Mar 17, 2025"; 'chart': "Mar 2025" (no day); 'full': same as display for now.
 * @returns {string|*} Formatted string, or the original dateStr if parsing fails (fallback).
 */
export function formatDate(dateStr, format = 'display') {
  const p = parseDateParts(dateStr);
  if (!p) return dateStr;

  const monthLabel = MONTHS_SHORT[p.monthIdx] ?? p.month;
  if (format === 'chart') {
    return `${monthLabel} ${p.year}`;
  }
  return `${monthLabel} ${p.day}, ${p.year}`;
}

// ─── Date ↔ String conversion ────────────────────────────────────────────────

/**
 * Convert a Date object to "YYYY-MM-DD" using local timezone (avoids UTC shift).
 * @param {Date} date
 * @returns {string}
 */
export function formatDateLocal(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Extract the date part "YYYY-MM-DD" from an ISO datetime string, converted to local timezone.
 * Also accepts a plain "YYYY-MM-DD" string (returned as-is).
 * @param {string} iso
 * @returns {string}
 */
export function toDatePart(iso) {
  if (!iso) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  if (iso.includes('T')) {
    const d = new Date(iso);
    return formatDateLocal(d);
  }
  return '';
}

/**
 * Extract the time part "HH:MM" from an ISO datetime string, converted to local timezone.
 * Also accepts a plain "HH:MM" string (returned as-is).
 * @param {string} iso
 * @returns {string}
 */
export function toTimePart(iso) {
  if (!iso) return '';
  if (/^\d{2}:\d{2}$/.test(iso)) return iso;
  if (iso.includes('T')) {
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }
  return '';
}

/**
 * Combine a local date string "YYYY-MM-DD" and time string "HH:MM" into a UTC ISO string.
 * Interprets the inputs as the user's local timezone, then converts to UTC for the server.
 * @param {string} date - "YYYY-MM-DD"
 * @param {string} time - "HH:MM"
 * @returns {string} ISO UTC string (e.g. "2026-03-17T15:00:00.000Z")
 */
export function toDateTime(date, time) {
  if (!date || !time) return '';
  const [year, month, day] = date.split('-').map(Number);
  const [hours, minutes] = time.split(':').map(Number);
  const localDate = new Date(year, month - 1, day, hours, minutes);
  return localDate.toISOString();
}

// ─── Date arithmetic ──────────────────────────────────────────────────────────

/**
 * Calculate the number of days between two "YYYY-MM-DD" date strings.
 * @param {string} startDate
 * @param {string} endDate
 * @returns {number}
 */
function calendarDayUtc(dateStr) {
  const p = parseDateParts(dateStr);
  if (!p) return null;
  return Date.UTC(Number(p.year), p.monthIdx, p.day);
}

export function calculateDuration(startDate, endDate) {
  const start = calendarDayUtc(startDate);
  const end = calendarDayUtc(endDate);
  if (start == null || end == null) return 0;
  return Math.round(Math.abs(end - start) / 86400000);
}

/**
 * Calculate the number of days from today until a future date string "YYYY-MM-DD".
 * Returns 0 if the date is in the past or invalid.
 * @param {string} futureDate
 * @returns {number}
 */
export function calculateDaysUntil(futureDate) {
  const future = calendarDayUtc(futureDate);
  if (future == null) return 0;
  const today = new Date();
  const todayUtc = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  const diffDays = Math.round((future - todayUtc) / 86400000);
  return diffDays > 0 ? diffDays : 0;
}
